import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { resolveShortLink, recordLinkClick } from '../services/sharingLinksService';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiLink, FiAlertCircle } = FiIcons;

const ShareLinkRedirect = () => {
  const { eventSlug, agentSlug } = useParams();
  const navigate = useNavigate();
  const [error, setError] = useState(null);

  useEffect(() => {
    const handleRedirect = async () => {
      try {
        console.log('=== ShareLinkRedirect Debug ===');
        console.log('Event slug from URL:', eventSlug);
        console.log('Agent slug from URL:', agentSlug);

        // Resolve the short link (slugs are case-insensitive; normalize to lowercase for lookups)
        const normEventSlug = (eventSlug && String(eventSlug).trim().toLowerCase()) || eventSlug;
        const normAgentSlug = (agentSlug && String(agentSlug).trim().toLowerCase()) || agentSlug;
        const { data, error: resolveError } = await resolveShortLink(normEventSlug, normAgentSlug);

        console.log('Resolve result - data:', data);
        console.log('Resolve result - error:', resolveError);

        if (resolveError || !data) {
          console.error('Link resolution failed:', resolveError);
          setError(resolveError?.message || 'Invalid or expired invitation link');
          return;
        }

        // Get IP address (placeholder for now - in production you'd get this from a backend)
        const ip = 'unknown';

        // Get user agent
        const userAgent = navigator.userAgent;

        // Record the click
        try {
          const { error: clickError } = await recordLinkClick(normAgentSlug, {
            ipAddress: ip,
            userAgent
          });

          if (clickError) {
            console.error('Failed to record click:', clickError);
            // Don't block redirect on tracking failure
          }
        } catch (trackingError) {
          console.error('Failed to record click:', trackingError);
          // Don't block redirect on tracking failure
        }

        // Redirect to landing page with ref parameter (ref is preserved when user clicks Register)
        navigate(`/landing/${data.event_id}?ref=${encodeURIComponent(normAgentSlug)}`, { replace: true });
      } catch (err) {
        console.error('Redirect error:', err);
        console.error('Error stack:', err.stack);
        console.error('Error name:', err.name);
        console.error('Error message:', err.message);
        setError('An error occurred while processing your link');
      }
    };

    handleRedirect();
  }, [eventSlug, agentSlug, navigate]);

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl shadow-xl border border-slate-200 p-8 max-w-md w-full text-center"
        >
          <div className="w-16 h-16 bg-rose-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <SafeIcon icon={FiAlertCircle} className="w-8 h-8 text-rose-600" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900 mb-2">Link Not Found</h1>
          <p className="text-slate-600 mb-6">{error}</p>
          <button
            onClick={() => navigate('/')}
            className="px-6 py-3 bg-slate-900 text-white rounded-lg hover:bg-slate-800 transition-all font-medium"
          >
            Go to Home
          </button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="bg-white rounded-2xl shadow-xl border border-slate-200 p-12 max-w-md w-full text-center"
      >
        <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-6 animate-pulse">
          <SafeIcon icon={FiLink} className="w-8 h-8 text-slate-600" />
        </div>
        <h1 className="text-2xl font-bold text-slate-900 mb-2">Redirecting...</h1>
        <p className="text-slate-600">Please wait while we redirect you to the event page</p>
        <div className="mt-8 flex justify-center">
          <div className="w-8 h-8 border-4 border-slate-200 border-t-slate-600 rounded-full animate-spin"></div>
        </div>
      </motion.div>
    </div>
  );
};

export default ShareLinkRedirect;
